import { type CSSProperties, type ReactNode, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { Modal } from '@/components/Modal';
import { ApiRequestError, apiFetch } from '@/lib/api/client';
import { runtimeConfig } from '@/lib/config';

/**
 * 헤더의 [메일함] — 하이웍스 메일을 <b>로그인 없이</b> 연다 (사용자 지시 2026-10-02 · DOC-108).
 *
 * <h2>누르면 한 번에 간다</h2>
 * 새 탭이 곧바로 `GET /v1/hiworks/sso/mail` 로 들어간다. 서버가 거기서 하이웍스 폼 한 벌을 만들어
 * 내리고, <b>그 탭이 하이웍스로 제출</b>한다 — 하이웍스가 그 브라우저에 쿠키를 심어야 로그인이
 * 되기 때문이다(서버가 대신 보내면 쿠키가 서버에 남는다 · 2026-10-02 운영 장애).
 *
 * <h2>비밀번호는 처음 한 번만</h2>
 * 누르는 순간 `POST /mail/open` 이 먼저 간다. 서버가 담아 둔 것 → 기본 비밀번호 순으로 하이웍스에
 * 묶어 보고, 둘 다 거절되면 PASSWORD_REQUIRED 에 하이웍스의 한 줄이 온다 — 그때 빈 탭을 닫고 이
 * 창이 떠서 비밀번호를 받는다. [연결] 도 서버가 먼저 쳐 보고, 틀리면 400 의 한 줄이 창에 남는다
 * (사용자 지시 「틀리면 모달」 2026-10-06). 맞으면 담고 새 탭을 연다. 그 뒤로는 묻지 않는다.
 *
 * <p>새 탭이 서버 답 없이 중계로 들어가 비밀번호가 필요해진 경우만 `/?hiworks=link` 로 되돌아온다.
 */

/**
 * `NO_ACCOUNT` — 하이웍스에 <b>그 아이디가 없다</b>(실측 2026-10-06). 비밀번호로 풀리지 않는다.
 * 예전에는 거절 한 줄(「유저 정보를 찾을수 없습니다」)만 적은 창을 띄웠는데 사람에게는 메일함이
 * 안 열리는 것이었다 — 지금은 그 탭을 중계로 보내고, 중계가 하이웍스 메일로 302 한다
 * (사용자 지시 2026-10-06 「실패했으면 그냥 메일함으로 리다이렉트」).
 */
type OpenResult = { status: 'READY' | 'PASSWORD_REQUIRED' | 'NO_ACCOUNT'; message: string | null };

const RELAY_PATH = '/v1/hiworks/sso/mail';

export function HiworksMailButton({
  className,
  style,
  onActivate,
  tour,
  children = '메일함',
}: {
  className?: string;
  style?: CSSProperties;
  /** 누르는 순간 먼저 할 일 — 서랍은 여기서 닫는다. */
  onActivate?: () => void;
  /**
   * {@code data-tour} 이름. 안내 투어가 짚는 자리이자 <b>AI 가 짚을 수 있는 자리</b>다
   * ({@link collectScreenAnchors}).
   *
   * <p>필요했던 이유: 운영 실패 목록의 「메일확인」(2026-10-02)이 <b>「도구 없음 · 화면 안내 없음」</b>
   * 으로 끝났다. 메일은 화면이 아니라 헤더의 버튼이라 안내할 경로가 없고(`agents/screens.py`),
   * 이름이 붙어 있지 않으면 대화가 그 자리를 가리킬 수도 없었다.
   */
  tour?: string;
  children?: ReactNode;
}) {
  const [asking, setAsking] = useState(false);
  const [password, setPassword] = useState('');
  /** 담아 둔 것·기본 비밀번호가 거절됐을 때 하이웍스가 준 한 줄. 모달에 그대로 적는다. */
  const [rejected, setRejected] = useState<string | null>(null);

  /*
    서버가 되돌려보낸 표시(`?hiworks=link`)를 보고 연다. 주소에서는 곧바로 지운다 — 새로고침할
    때마다 다시 뜨면 화면을 떠날 수 없다.
  */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('hiworks') !== 'link') return;
    params.delete('hiworks');
    const rest = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${rest ? `?${rest}` : ''}`);
    setAsking(true);
  }, []);

  const link = useMutation({
    mutationFn: (value: string) =>
      apiFetch<OpenResult>('/v1/hiworks/mail/link', { method: 'POST', body: { password: value } }),
  });

  const open = useMutation({
    mutationFn: () => apiFetch<OpenResult>('/v1/hiworks/mail/open', { method: 'POST' }),
  });

  /**
   * 누르는 순간 <b>빈 탭을 먼저 연다</b> — 답을 기다린 뒤 열면 팝업 차단에 걸린다.
   *
   * <p>비밀번호가 필요하면 그 탭을 닫고 <b>보고 있던 화면에서</b> 묻는다. 예전에는 서버가 새 탭을
   * 우리 홈으로 돌려보냈는데, 사람 눈에는 「눌렀더니 홈으로 돌아나왔다」 로만 보였다
   * (사용자 지적 2026-10-02).
   */
  const start = async () => {
    onActivate?.();
    const tab = window.open('', '_blank');
    try {
      const result = await open.mutateAsync();
      if (result.status !== 'PASSWORD_REQUIRED') {
        // READY 는 하이웍스로, NO_ACCOUNT 는 중계가 하이웍스 메일로 302 한다.
        landOn(tab, `${runtimeConfig().apiBaseUrl}${RELAY_PATH}`);
        return;
      }
      tab?.close();
      link.reset();
      setPassword('');
      setRejected(result.message);
      setAsking(true);
    } catch {
      // 서버가 답하지 않으면 중계가 알아서 하이웍스로 보낸다 — 그 탭을 그대로 보낸다.
      landOn(tab, `${runtimeConfig().apiBaseUrl}${RELAY_PATH}`);
    }
  };

  const submit = async () => {
    if (!password.trim() || link.isPending) return;
    try {
      await link.mutateAsync(password);
      setAsking(false);
      setPassword('');
      // 「연결」 을 누른 손짓 안이라 여기서 여는 것은 팝업 차단에 걸리지 않는다.
      window.open(`${runtimeConfig().apiBaseUrl}${RELAY_PATH}`, '_blank', 'noopener');
    } catch (error) {
      // 아이디가 없는 거절은 다시 적어도 같다 — 하이웍스 메일로 보낸다(중계가 302).
      if (error instanceof ApiRequestError && error.apiError?.code === 'HIWORKS_NO_ACCOUNT') {
        setAsking(false);
        setPassword('');
        window.open(`${runtimeConfig().apiBaseUrl}${RELAY_PATH}`, '_blank', 'noopener');
      }
      /* 그 밖의 실패 사유는 창에 그대로 남는다 */
    }
  };

  return (
    <>
      <button
        onClick={() => void start()}
        disabled={open.isPending}
        className={className}
        style={style}
        data-tour={tour}
      >
        {children}
      </button>

      {asking && (
        <Modal
          title="하이웍스 연동"
          maxWidth="400px"
          onClose={() => setAsking(false)}
          closeDisabled={link.isPending}
          actions={(
            <button className="btn-primary" onClick={() => void submit()} disabled={!password.trim() || link.isPending}>
              {link.isPending ? '연결 중…' : '연결'}
            </button>
          )}
        >
          <form
            className="space-y-2"
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <input
              className="input-field"
              type="password"
              autoComplete="off"
              aria-label="하이웍스 비밀번호"
              placeholder="하이웍스 비밀번호"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setRejected(null);
                link.reset();
              }}
            />
            {(link.isError || rejected) && (
              <p style={{ fontSize: '12px', color: 'var(--danger)' }}>{link.isError ? link.error.message : rejected}</p>
            )}
          </form>
        </Modal>
      )}
    </>
  );
}

/** 열어 둔 빈 탭을 그 주소로 보낸다. 탭이 없으면(차단) 그냥 연다. */
function landOn(tab: Window | null, url: string) {
  if (tab) {
    tab.opener = null;
    tab.location.href = url;
    return;
  }
  window.open(url, '_blank', 'noopener');
}
